import React, { useState, useEffect, useRef } from "react";
import SeatGrid from "./SeatGrid";
import BookingForm from "./BookingForm";
import SectionBookingsList from "./SectionBookingsList";
import SeatEditForm from "./SeatEditForm";
import SeatBookingDetails from "./SeatBookingDetails";
import { ConfirmDialog } from "../../components/Dialogs";
import { formatAdminDisplayDate } from "../utils";

const normalizeValue = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const isBlockedSeatBooking = (booking = {}) =>
  normalizeValue(
    booking.booking_details?.restriction_type ||
      booking.restriction_type ||
      booking.booking_details?.type ||
      booking.type,
  ) === "block" ||
  (normalizeValue(booking.user_name || booking.username || booking.userName) === "n/a" &&
    !booking.user &&
    !booking.user_id) ||
  normalizeValue(booking.source) === "seatblock";

const SectionManagementModal = ({
  selectedSection,
  users,
  setIsSectionModalOpen,
  sectionBookings,
  workspaceBookings,
  activeSeatSlot,
  newSeatLabel,
  setNewSeatLabel,
  newSeatType,
  setNewSeatType,
  bookingForm,
  setBookingForm,
  handleSeatNameSubmit,
  handleUpdateSeat,
  handleDeleteSeat,
  handleEmptySlotClick,
  handleCreateBooking,
  handleRemoveDate,
  handleBookingDateChange,
  handleToggleWeekday,
  handleCancelBooking,
  previewDateCount,
  seatLabelInputRef,
  loadSeatDetails,
  selectedSeatDetails,
  filterDate,
  loadSectionBookings,
  mode = "seats",
}) => {
  const [selectedSeatId, setSelectedSeatId] = useState(bookingForm.seatId || null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const bookingScheduleRef = useRef(null);
  // Guard ref: track which seatId was last fetched so the effect
  // does NOT re-fire simply because the parent re-renders and
  // passes a new loadSeatDetails function reference.
  const lastFetchedSeatId = useRef(null);

  const scrollToSchedule = () => {
    setTimeout(() => {
      bookingScheduleRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  useEffect(() => {
    if (selectedSeatId && selectedSeatId !== lastFetchedSeatId.current) {
      lastFetchedSeatId.current = selectedSeatId;
      setLoadingDetails(true);
      loadSeatDetails(selectedSeatId).finally(() => setLoadingDetails(false));
    }
    if (!selectedSeatId) {
      lastFetchedSeatId.current = null;
    }
  }, [selectedSeatId, loadSeatDetails]);

  useEffect(() => {
    if (selectedSeatId && bookingForm.seatId !== selectedSeatId) {
      setBookingForm((prev) => ({ ...prev, seatId: selectedSeatId }));
    }
  }, [selectedSeatId, bookingForm.seatId, setBookingForm]);

  const selectedSeat = selectedSection.seats.find((s) => s.id === selectedSeatId);

  const deselect = () => {
    setSelectedSeatId(null);
    setBookingForm((prev) => ({ ...prev, seatId: "" }));
  };

  const handleCreateBookingAndRefresh = async (e) => {
    const success = await handleCreateBooking(e);
    if (success && selectedSeatId) {
      lastFetchedSeatId.current = selectedSeatId;
      setLoadingDetails(true);
      await loadSeatDetails(selectedSeatId).finally(() => setLoadingDetails(false));
    }
  };

  const onUpdateSeat = async (seatData) => {
    await handleUpdateSeat(selectedSeatId, seatData);
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-slate-950/35"
        onClick={() => setIsSectionModalOpen(false)}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center ">
        <div className="max-h-[90vh] w-full max-w-9xl overflow-auto border border-slate-200 bg-white p-6 shadow-2xl md:mx-5">
          {/* Modal header */}
          <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                {mode === "seats" ? "Section Seats Layout" : "Section Bookings View"}
              </p>
              <h2 className="mt-3 text-[26px] xl:text-[30px] font-medium text-slate-950">
                {selectedSection.name} {mode === "seats" ? "— Manage Seats" : "— Manage Bookings"}
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                {mode === "seats"
                  ? "Click an empty slot to add a seat. Click an existing seat to edit details or delete."
                  : "Select an active seat to manage bookings or view current occupancy status."}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsSectionModalOpen(false)}
              className="rounded-2xl border border-slate-200 px-6 py-3 text-sm font-bold text-slate-950 hover:bg-slate-50"
            >
              Close
            </button>
          </div>

          <div className="mt-8 grid gap-8 xl:grid-cols-[1.50fr_0.75fr]">
            {/* Left Column: Seat grid & Booking schedule */}
            <div className="space-y-6">
              <SeatGrid
                selectedSection={selectedSection}
                activeSeatSlot={activeSeatSlot}
                newSeatLabel={newSeatLabel}
                setNewSeatLabel={setNewSeatLabel}
                newSeatType={newSeatType}
                setNewSeatType={setNewSeatType}
                bookingForm={bookingForm}
                setBookingForm={setBookingForm}
                handleSeatNameSubmit={handleSeatNameSubmit}
                handleDeleteSeat={handleDeleteSeat}
                handleEmptySlotClick={handleEmptySlotClick}
                seatLabelInputRef={seatLabelInputRef}
                selectedSeatId={selectedSeatId}
                setSelectedSeatId={setSelectedSeatId}
                onEditSeat={setSelectedSeatId}
                mode={mode}
              />

              {mode === "bookings" && selectedSeat && (
                <SeatBookingSchedule
                  selectedSeat={selectedSeat}
                  details={selectedSeatDetails}
                  onCancel={handleCancelBooking}
                  bookingScheduleRef={bookingScheduleRef}
                />
              )}
            </div>

            {/* Right panel */}
            <div className="space-y-6">
              {mode === "seats" ? (
                /* ── MANAGE SEATS MODE ── */
                !selectedSeat ? (
                  <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-8 text-center">
                    <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
                    </div>
                    <p className="text-sm font-semibold text-slate-700">No Seat Selected</p>
                    <p className="mt-1 text-xs text-slate-400">
                      Click an empty slot to add a new seat, or select an existing seat on the grid to configure or delete it.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="rounded-[24px] border border-slate-200 bg-white px-6 py-6 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                          Selected Seat
                        </p>
                        <p className="mt-1 text-xl font-medium text-slate-950">{selectedSeat.label}</p>
                        <span className="inline-block rounded-md bg-slate-100 px-2 py-0.5 text-[9px] font-black tracking-widest uppercase text-slate-500 mt-1">
                          {selectedSeat.type}
                        </span>
                      </div>
                      
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleDeleteSeat(selectedSeatId)}
                          className="rounded-xl border border-rose-100 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-600 transition-all hover:bg-rose-100"
                        >
                          Delete
                        </button>
                        <button
                          type="button"
                          onClick={deselect}
                          className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-500 hover:bg-slate-50"
                        >
                          Deselect
                        </button>
                      </div>
                    </div>

                    <SeatEditForm
                      selectedSeat={selectedSeat}
                      onUpdate={onUpdateSeat}
                      onCancel={deselect}
                    />
                  </>
                )
              ) : (
                /* ── MANAGE BOOKINGS MODE ── */
                !selectedSeat ? (
                  <SectionBookingsList
                    sectionBookings={sectionBookings}
                    selectedSection={selectedSection}
                    handleCancelBooking={handleCancelBooking}
                    selectedSeatId={null}
                    users={users}
                    filterDate={filterDate}
                    loadSectionBookings={loadSectionBookings}
                  />
                ) : (
                  <>
                    <div className="rounded-[24px] border border-slate-200 bg-white px-6 py-6 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                          Selected Seat
                        </p>
                        <p className="mt-1 text-xl font-medium text-slate-950">{selectedSeat.label}</p>
                        <span className="inline-block rounded-md bg-slate-100 px-2 py-0.5 text-[9px] font-black tracking-widest uppercase text-slate-500 mt-1">
                          {selectedSeat.type}
                        </span>
                      </div>
                      
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={deselect}
                          className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-500 hover:bg-slate-50"
                        >
                          Deselect
                        </button>
                      </div>
                    </div>

                    {/* Detailed booking view (occupancy status) */}
                    <SeatBookingDetails 
                      details={selectedSeatDetails} 
                      loading={loadingDetails} 
                      onCancel={handleCancelBooking}
                      onViewAllClick={scrollToSchedule}
                    />

                    <BookingForm
                      selectedSection={selectedSection}
                      users={users}
                      bookingForm={bookingForm}
                      setBookingForm={setBookingForm}
                      previewDateCount={previewDateCount}
                      handleCreateBooking={handleCreateBookingAndRefresh}
                      handleRemoveDate={handleRemoveDate}
                      handleBookingDateChange={handleBookingDateChange}
                      handleToggleWeekday={handleToggleWeekday}
                      sectionBookings={sectionBookings}
                      workspaceBookings={workspaceBookings}
                      selectedSeatId={selectedSeatId}
                      selectedSeatDetails={selectedSeatDetails}
                    />
                  </>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SectionManagementModal;

const SeatBookingSchedule = ({ selectedSeat, details, onCancel, bookingScheduleRef }) => {
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false });
  if (!details || !details.bookings || details.bookings.length === 0) return null;

  const bookings = [...details.bookings].sort((a, b) => {
    const dateA = a.start_time ? a.start_time.split("T")[0] : (a.date || "");
    const dateB = b.start_time ? b.start_time.split("T")[0] : (b.date || "");
    return dateA.localeCompare(dateB);
  });
  return (
    <div 
      ref={bookingScheduleRef} 
      className=" border border-slate-200 bg-slate-50 p-8  scroll-mt-6"
    >
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div>
          <h3 className="text-xl font-medium text-slate-950">
            Booking Timeline: Seat {selectedSeat.label}
          </h3>
          <p className="mt-1 text-xs text-slate-400">
            A complete listing of all reserved dates and schedules for this seat.
          </p>
        </div>
        <span className="rounded-full bg-slate-200 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-700">
          {bookings.length} reservation{bookings.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="mt-6 max-h-[400px] overflow-auto pr-2 space-y-3">
        {bookings.map((b) => {
          const type = b.booking_type || "One-Time";
          const isBlocked = isBlockedSeatBooking(b);
          const isPermanent = type.toLowerCase() === "permanent";
          const isRecurring = type.toLowerCase() === "recurring";
          
          const isDateRange = type.toLowerCase() === "date-range" || type.toLowerCase() === "date range";
          
          const formattedPermanentDate = b.start_time
            ? formatAdminDisplayDate(b.start_time)
            : b.created_at || b.createdAt
              ? formatAdminDisplayDate(b.created_at || b.createdAt)
              : (b.date && b.date !== "permanent" ? formatAdminDisplayDate(b.date) : "");

          const bookingDate = isPermanent 
            ? (formattedPermanentDate || "—")
            : b.start_time 
              ? formatAdminDisplayDate(b.start_time)
              : (b.date ? formatAdminDisplayDate(b.date) : "—");

          const bId = b.booking_id || b.id || b._id;

          return (
            <div 
              key={bId + "-" + (b.start_time || b.date || Math.random())}
              className={`flex items-center justify-between gap-4 rounded-2xl border bg-white p-4 shadow-sm hover:shadow-md transition-all ${
                isBlocked ? "border-rose-100 bg-rose-50/40" : "border-slate-100"
              }`}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">
                  {isBlocked ? "Blocked" : (b.user_name || b.username || "Unknown User")}
                </p>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  {isBlocked ? (
                    <>
                      <span className="rounded-md bg-rose-100 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-rose-600">
                        Blocked
                      </span>
                      <span className={`rounded-md px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ${
                        isPermanent 
                          ? "bg-indigo-100 text-indigo-600" 
                          : isRecurring 
                            ? "bg-amber-100 text-amber-600"
                            : isDateRange
                              ? "bg-emerald-100 text-emerald-600"
                              : "bg-slate-100 text-slate-500"
                      }`}>
                        {type}
                      </span>
                    </>
                  ) : (
                    <span className={`rounded-md px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ${
                      isPermanent 
                        ? "bg-indigo-100 text-indigo-600" 
                        : isRecurring 
                          ? "bg-amber-100 text-amber-600"
                          : isDateRange
                            ? "bg-emerald-100 text-emerald-600"
                            : "bg-slate-100 text-slate-500"
                    }`}>
                      {type}
                    </span>
                  )}
                  <span className="text-xs font-semibold text-slate-400">
                    {bookingDate}
                  </span>
                </div>
              </div>
              
              <button
                type="button"
                onClick={() => {
                  setConfirmDialog({
                    isOpen: true,
                    title: "Cancel Booking",
                    message: `Are you sure you want to cancel only this ${isBlocked ? "blocked " : ""}${type.toLowerCase()} booking?`,
                    isDestructive: true,
                    onConfirm: () => {
                      setConfirmDialog({ isOpen: false });
                      onCancel(bId, {
                        bookingType: type,
                        isSeatBlock: isBlocked,
                        forceStandard: !isBlocked,
                        useIndividualBlockCancel: isBlocked,
                      });
                    },
                    onCancel: () => setConfirmDialog({ isOpen: false }),
                  });
                }}
                className="rounded-xl border border-rose-100 bg-rose-50/50 px-3 py-2 text-[10px] font-bold text-rose-600 shadow-sm transition-all hover:bg-rose-50"
              >
                Cancel
              </button>
            </div>
          );
        })}
      </div>
      
      <ConfirmDialog {...confirmDialog} />
    </div>
  );
};
